import React from "react";

/**
 * Centralised SVG icon library.
 * Usage: <Icon name="copy" size={16} className="text-accent" />
 */

type IconName =
  | "copy"
  | "check"
  | "close"
  | "edit"
  | "pdf"
  | "camera"
  | "text"
  | "csv"
  | "qr"
  | "cart"
  | "sun"
  | "moon"
  | "arrow-left"
  | "arrow-right"
  | "chevron-up"
  | "chevron-down"
  | "trash"
  | "settings"
  | "analytics"
  | "history"
  | "note"
  | "print"
  | "user";

interface IconProps {
  name: IconName;
  size?: number;
  className?: string;
  strokeWidth?: number;
}

const paths: Record<IconName, React.ReactNode> = {
  copy: (
    <>
      <rect x="9" y="9" width="13" height="13" rx="2" strokeWidth="1.2" />
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" strokeWidth="1.2" />
    </>
  ),
  check: <path d="M20 6L9 17l-5-5" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" />,
  close: (
    <path d="M18 6L6 18M6 6l12 12" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" />
  ),
  edit: (
    <path
      d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"
      strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.2"
    />
  ),
  pdf: (
    <>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" strokeWidth="1.2" />
      <polyline points="14 2 14 8 20 8" strokeWidth="1.2" />
      <line x1="8" y1="13" x2="16" y2="13" strokeWidth="1" />
      <line x1="8" y1="17" x2="16" y2="17" strokeWidth="1" />
    </>
  ),
  camera: (
    <>
      <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" strokeWidth="1.2" />
      <circle cx="12" cy="13" r="4" strokeWidth="1.2" />
    </>
  ),
  text: (
    <>
      <line x1="17" y1="10" x2="3" y2="10" strokeWidth="1.2" />
      <line x1="21" y1="6" x2="3" y2="6" strokeWidth="1.2" />
      <line x1="21" y1="14" x2="3" y2="14" strokeWidth="1.2" />
      <line x1="17" y1="18" x2="3" y2="18" strokeWidth="1.2" />
    </>
  ),
  csv: (
    <>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" strokeWidth="1.2" />
      <polyline points="14 2 14 8 20 8" strokeWidth="1.2" />
      <line x1="8" y1="13" x2="16" y2="13" strokeWidth="1" />
      <line x1="8" y1="17" x2="12" y2="17" strokeWidth="1" />
    </>
  ),
  qr: (
    <>
      <rect x="3" y="3" width="7" height="7" rx="1" strokeWidth="1.2" />
      <rect x="14" y="3" width="7" height="7" rx="1" strokeWidth="1.2" />
      <rect x="3" y="14" width="7" height="7" rx="1" strokeWidth="1.2" />
      <rect x="5" y="5" width="3" height="3" fill="currentColor" stroke="none" />
      <rect x="16" y="5" width="3" height="3" fill="currentColor" stroke="none" />
      <rect x="5" y="16" width="3" height="3" fill="currentColor" stroke="none" />
      <path d="M14 14h3v3M14 20h7M17 17h4" strokeLinecap="round" strokeWidth="1.2" />
    </>
  ),
  cart: (
    <>
      <circle cx="9" cy="21" r="1" />
      <circle cx="20" cy="21" r="1" />
      <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.2" />
    </>
  ),
  sun: (
    <>
      <circle cx="12" cy="12" r="5" strokeWidth="1.5" />
      {[0, 45, 90, 135, 180, 225, 270, 315].map((a) => {
        const rad = (a * Math.PI) / 180;
        const x1 = 12 + 7 * Math.cos(rad);
        const y1 = 12 + 7 * Math.sin(rad);
        const x2 = 12 + 9 * Math.cos(rad);
        const y2 = 12 + 9 * Math.sin(rad);
        return <line key={a} x1={x1} y1={y1} x2={x2} y2={y2} strokeWidth="1.5" strokeLinecap="round" />;
      })}
    </>
  ),
  moon: <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" strokeWidth="1.5" />,
  "arrow-left": <path d="M19 12H5M12 19l-7-7 7-7" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" />,
  "arrow-right": <path d="M5 12h14M12 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" />,
  "chevron-up": <polyline points="18 15 12 9 6 15" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" />,
  "chevron-down": <polyline points="6 9 12 15 18 9" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" />,
  trash: (
    <>
      <polyline points="3 6 5 6 21 6" strokeWidth="1.2" />
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6M10 11v6M14 11v6" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.2" />
      <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" strokeWidth="1.2" />
    </>
  ),
  settings: (
    <>
      <circle cx="12" cy="12" r="3" strokeWidth="1.2" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" strokeWidth="1.2" />
    </>
  ),
  analytics: (
    <>
      <line x1="18" y1="20" x2="18" y2="10" strokeWidth="1.2" strokeLinecap="round" />
      <line x1="12" y1="20" x2="12" y2="4" strokeWidth="1.2" strokeLinecap="round" />
      <line x1="6" y1="20" x2="6" y2="14" strokeWidth="1.2" strokeLinecap="round" />
    </>
  ),
  history: (
    <>
      <polyline points="12 8 12 12 14 14" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" />
      <path d="M3.05 11a9 9 0 1 1 .5 4M3 21v-4h4" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" />
    </>
  ),
  note: (
    <>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" strokeWidth="1.2" />
      <polyline points="14 2 14 8 20 8" strokeWidth="1.2" />
      <line x1="8" y1="13" x2="16" y2="13" strokeWidth="1" />
      <line x1="8" y1="17" x2="12" y2="17" strokeWidth="1" />
    </>
  ),
  print: (
    <>
      <polyline points="6 9 6 2 18 2 18 9" strokeWidth="1.2" />
      <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" strokeWidth="1.2" />
      <rect x="6" y="14" width="12" height="8" strokeWidth="1.2" />
    </>
  ),
  user: (
    <>
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx="12" cy="7" r="4" strokeWidth="1.2" />
    </>
  ),
};

export function Icon({ name, size = 16, className = "", strokeWidth }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth ?? 1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      {paths[name]}
    </svg>
  );
}
